import { useTranslate } from '@tolgee/react';
import { Button, Checkbox, Form, Input, message, Modal, ModalProps, Radio } from 'antd';
import CheckContainer from 'components/CheckContainer';
import FormField from 'components/FormField';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { authERP, generalAuthUrl, updateERP } from './service';
import nimInst from 'services/nim';
import { storesCache, useERPState, useRefreshERP } from './state';
import useModal from 'hooks/useModal';
import { WmModal } from './WmForm';
import styled from '@emotion/styled';
import {
  Store,
  StoreScopesModal,
  StoreScopesModalProps,
  StoreScopesPreview,
} from 'components/StoreScopes';
import { removeStoreScopesAll, stores2storeScopes } from 'views/BootGuide/views/AiModel/share';
import {
  storeIds2stores,
  storeScopes2storeIds,
} from '@knowledge/knowledges/general/StoreGroup/share';
import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import { Preview } from '@knowledge/Activity/share';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import { getAdminStores } from 'services/api/admin';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { getStoreMap } from '@knowledge/knowledges/general/utils';

type FV = Pick<
  ERP.ERP,
  'erpType' | 'appId' | 'name' | 'storeIds' | 'itemQueryType' | 'reissueItemQueryType'
> &
  Pick<ERP.QIANYI, 'secret'> &
  Pick<ERP.JUSHUITAN, 'openLogisticsApi'> & { id?: string };

const defaultInitialValues: FV = {
  erpType: 'JUSHUITAN',
  appId: '',
  secret: '',
  name: '',
  openLogisticsApi: false,
  storeIds: [],
  itemQueryType: 1,
  reissueItemQueryType: 2,
};

function ERPModal({
  mode,
  erp,
  storeMap,
  visible,
  onCancel,
  onUpdate,
  ...otherProps
}: ModalProps & {
  mode?: 'store';
  erp?: ERP.ERP;
  storeMap: Record<number, Store>;
  onUpdate: () => any;
}) {
  const { t } = useTranslate();

  const ref = useRef<any>(null);

  useEffect(() => {
    if (visible) {
      ref.current?.focus();
    }
  }, [visible]);

  const { auth, wmModal } = useAuth();

  const storeScopesModalState = useModal<StoreScopesModalProps>();

  const storeScopesModal = (
    <StoreScopesModal
      visible={storeScopesModalState.visible}
      onCancel={() => storeScopesModalState.hide()}
      {...storeScopesModalState.meta}
    />
  );

  const initialValues = useMemo(() => {
    if (!erp) {
      return defaultInitialValues;
    }
    return {
      ...erp,
      openLogisticsApi: erp.erpType === 'JUSHUITAN' ? erp.openLogisticsApi : false,
      secret: '',
    };
  }, [erp]);

  const erpModal = (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        const {
          id,
          erpType,
          appId,
          secret,
          name,
          storeIds,
          openLogisticsApi,
          itemQueryType,
          reissueItemQueryType,
        } = values;
        if (id) {
          return updateERP({
            id,
            openLogisticsApi,
            itemQueryType,
            reissueItemQueryType,
            storeIds,
          })
            .then(() => {
              onUpdate();
              message.success(t('phrase.保存成功'));
              onCancel?.({} as any);
              resetForm();
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        }
        if (erpType === 'QIANYI') {
          return authERP({
            erpType,
            appId,
            secret,
            name,
            storeIds,
            itemQueryType,
            reissueItemQueryType,
          })
            .then((res: any) => {
              if (res.data.code === 1068) {
                message.error(t('Secret Key错误'));
              } else if (res.data.code === 1069) {
                message.error(t('ERP授权不存在'));
              } else if (res.data.code === 1071) {
                message.error(t('ERP授权权限不足'));
              } else {
                onUpdate();
                message.success(t('authShop.授权成功'));
                onCancel?.({} as any);
                resetForm();
              }
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        }
        if (erpType === 'JUSHUITAN') {
          return auth({
            erpType,
            openLogisticsApi,
            itemQueryType,
            reissueItemQueryType,
            storeIds,
          })
            .then(() => {
              onCancel?.({} as any);
              resetForm();
            })
            .catch(() => {
              message.error(t('phrase.获取数据失败，请稍后再试'));
            });
        }
        return Promise.resolve();
      }}
    >
      {({
        values: { id, erpType, storeIds },
        setValues,
        isSubmitting,
        handleSubmit,
        resetForm,
      }) => {
        const isEdit = Boolean(id);

        if (mode === 'store') {
          return (
            <StoreScopesModal
              showFooterTips={false}
              visible={visible}
              storeDataSource={Object.values(storeMap)}
              value={stores2storeScopes(storeIds2stores(storeIds, storeMap))}
              onChange={(storeScopes) => {
                setValues((values) => ({
                  ...values,
                  storeIds: storeScopes2storeIds(
                    removeStoreScopesAll(storeScopes, Object.values(storeMap)),
                  ),
                }));
                handleSubmit();
              }}
              onCancel={() => {
                onCancel?.({} as any);
                resetForm();
              }}
            />
          );
        }
        return (
          <Modal
            visible={visible}
            title={isEdit ? t('marketing.编辑') : t('knowledge.新增x', { x: 'ERP' })}
            width={380}
            onCancel={(e) => {
              onCancel?.(e);
              resetForm();
            }}
            okText={erpType === 'JUSHUITAN' && !erp ? t('去授权') : t('保存')}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            {...otherProps}
          >
            <Form layout="vertical">
              {!isEdit && (
                <FormField<FV['erpType']> name="erpType" required label={t('选择ERP')}>
                  {({ field: { value, onChange } }) => {
                    return (
                      <CheckWrapper>
                        <CheckContainer
                          checked={value === 'JUSHUITAN'}
                          onChange={() => onChange('JUSHUITAN')}
                        >
                          {t('聚水潭ERP')}
                        </CheckContainer>
                        <CheckContainer
                          checked={value === 'QIANYI'}
                          onChange={() => onChange('QIANYI')}
                        >
                          {t('千易ERP')}
                        </CheckContainer>
                      </CheckWrapper>
                    );
                  }}
                </FormField>
              )}
              {erpType === 'JUSHUITAN' && (
                <FormField<FV['openLogisticsApi']> name="openLogisticsApi">
                  {({ field: { value, onChange } }) => {
                    return (
                      <Checkbox
                        checked={value}
                        onChange={({ target: { checked } }) => onChange(checked)}
                      >
                        {t('已在聚水潭开通跨境物流轨迹查询接口')}
                      </Checkbox>
                    );
                  }}
                </FormField>
              )}
              {erpType === 'QIANYI' &&
                !isEdit && [
                  <FormField<FV['appId']>
                    name="appId"
                    required
                    label="Access Id"
                    validate={(value) =>
                      !value.trim() ? t('department.请输入x', { x: 'Access Id' }) : undefined
                    }
                  >
                    {({ field }) => {
                      return (
                        <Input
                          ref={ref}
                          showCount
                          maxLength={30}
                          placeholder={t('department.请输入x', { x: 'Access Id' })}
                          {...field}
                        />
                      );
                    }}
                  </FormField>,
                  <FormField<FV['secret']>
                    name="secret"
                    required
                    label="Secret Key"
                    validate={(value) =>
                      !value ? t('department.请输入x', { x: 'Secret Key' }) : undefined
                    }
                  >
                    {({ field }) => {
                      return (
                        <Input.Password
                          placeholder={t('department.请输入x', { x: 'Secret Key' })}
                          {...field}
                        />
                      );
                    }}
                  </FormField>,
                ]}
              {!isEdit && (
                <FormField<FV['storeIds']>
                  name="storeIds"
                  label={t('绑定店铺')}
                  validate={(storeIds) =>
                    storeIds.length > 0 ? undefined : t('department.请选择x', { x: t('绑定店铺') })
                  }
                >
                  {({ field: { value, onChange } }) => {
                    const storeScopes = stores2storeScopes(storeIds2stores(value, storeMap));
                    const onEdit = () =>
                      storeScopesModalState.show({
                        showFooterTips: false,
                        value: storeScopes,
                        storeDataSource: Object.values(storeMap),
                        storeMap,
                        onChange: (storeScopes) =>
                          onChange(
                            storeScopes2storeIds(
                              removeStoreScopesAll(storeScopes, Object.values(storeMap)),
                            ),
                          ),
                      });
                    return (
                      <StoreWrapper>
                        {storeScopesModal}
                        {value.length === 0 ? (
                          <Button type="link" icon={<PlusOutlined />} onClick={onEdit}>
                            {t('添加店铺')}
                          </Button>
                        ) : (
                          <div>
                            {t('x家', { count: value.length })}
                            <Preview
                              content={
                                <PreviewWrapper>
                                  <StoreScopesPreview
                                    value={storeScopes}
                                    showSearch={false}
                                    showTitle={false}
                                    storeMap={storeMap}
                                  />
                                </PreviewWrapper>
                              }
                            />
                            <Button type="link" icon={<FormOutlined />} onClick={onEdit}>
                              {t('marketing.编辑')}
                            </Button>
                          </div>
                        )}
                      </StoreWrapper>
                    );
                  }}
                </FormField>
              )}
              <FormField<FV['itemQueryType']>
                name="itemQueryType"
                required
                label={t('选择商品-库存状态查询方式：')}
              >
                {({ field: { value, onChange } }) => {
                  return (
                    <div>
                      <Radio
                        checked={value === 1}
                        onChange={({ target: { checked } }) => checked && onChange(1)}
                      >
                        {t('根据仓库所在国家')}
                      </Radio>
                      <Radio
                        checked={value === 2}
                        onChange={({ target: { checked } }) => checked && onChange(2)}
                      >
                        {t('仓库绑定到店铺')}
                      </Radio>
                    </div>
                  );
                }}
              </FormField>
              <FormField<FV['reissueItemQueryType']>
                name="reissueItemQueryType"
                required
                label={t('选择待办-补发库存查询方式：')}
              >
                {({ field: { value, onChange } }) => {
                  return (
                    <div>
                      <Radio
                        checked={value === 1}
                        onChange={({ target: { checked } }) => checked && onChange(1)}
                      >
                        {t('根据仓库所在国家')}
                      </Radio>
                      <Radio
                        checked={value === 2}
                        onChange={({ target: { checked } }) => checked && onChange(2)}
                      >
                        {t('仓库绑定到店铺')}
                      </Radio>
                    </div>
                  );
                }}
              </FormField>
            </Form>
            {erpType === 'JUSHUITAN' && (
              <Alert>
                {t(
                  '同一聚水潭主账号下只能存在单个授权关系，主仓（授权时不选仓库，主仓授权可以获取主仓和分仓的全部数据）或某一分仓（授权时选择分仓，分仓授权只能获取对应分仓的数据），再次授权时新授权关系和有效期会覆盖旧授权，旧授权失效',
                )}
              </Alert>
            )}
          </Modal>
        );
      }}
    </Formik>
  );

  return (
    <>
      {erpModal}
      {wmModal}
    </>
  );
}

export function useAuth() {
  const { refreshERP } = useRefreshERP();
  const { erpList } = useERPState();

  const { visible, show, hide, meta } = useModal<{
    initId: string;
    storeMap: Record<number, Store>;
  }>();

  const wmModal = meta && (
    <WmModal key={meta.initId} {...meta} visible={visible} onCancel={() => hide()} />
  );

  const showWmModal = useCallback(
    (id: string) => {
      return (
        storesCache.current
          ? Promise.resolve(storesCache.current)
          : getAdminStores({
              page: 1,
              pageSize: 999999,
              permId: permissionNameIdMap.店铺管理,
            }).then((res) => {
              const stores = res.data.data.stores;
              storesCache.current = stores;
              return stores;
            })
      )
        .then((stores) => {
          show({
            initId: id,
            storeMap: getStoreMap(stores as any) as any,
          });
        })
        .catch(() => {});
    },
    [show],
  );

  const auth = useCallback(
    (body: Parameters<typeof generalAuthUrl>[0]) => {
      return generalAuthUrl(body).then((url) => {
        window.open(url, '_blank');
        nimInst.on('sys:message:auth_success', ({ id }) => {
          const isReAuth = erpList.length > 0;
          if (!isReAuth) {
            showWmModal(id);
          }
          refreshERP();
        });
      });
    },
    [showWmModal, erpList, refreshERP],
  );

  return {
    showWmModal,
    auth,
    wmModal,
  };
}

const CheckWrapper = styled.div`
  > label {
    margin-right: 6px;
  }
`;

const StoreWrapper = styled.div`
  padding: 2px 14px;
  background: var(--fett-erp-card-bg);
  > button {
    padding-left: 0;
  }
`;

const Alert = styled.div`
  margin: 0 -24px -24px;
  padding: 8px 16px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
  background: var(--fett-surface-warning-bg);
`;

export default ERPModal;
